import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AuthType, GetCurrentAuthType } from '@shared/utils/AuthUtils';
import { DialogIdentifiers, openDialog } from "../store/dialog.duck";
import {
  requestCalendarAccess,
  selectCalendarAccess,
  selectLifecycle,
} from '@shared/auth/authMachine.duck';
import { CalendarAccess } from '@shared/auth/types';
import { selectCurrentUser } from "@shared/store/auth.duck";


export enum SyncStatus {
  Active, Failed, Pending, Disabled, NA
}

function calendarAccessToSyncStatus(access: CalendarAccess, lifecycle: string, authType: AuthType | null): SyncStatus {
  if (authType !== AuthType.Microsoft) {
    return SyncStatus.NA;
  }
  if (lifecycle !== 'sessionReady') {
    return SyncStatus.Pending;
  }
  switch (access) {
    case 'ready':
      return SyncStatus.Active;
    case 'pending':
      return SyncStatus.Pending;
    case 'unknown':
      return SyncStatus.Failed;
    case 'consentRequired':
      return SyncStatus.Failed;
    case 'disabled':
      return SyncStatus.Disabled;
    default:
      return SyncStatus.Pending;
  }
}

export function WithMicrosoftCalendarSyncActiveChecker() {
  const authType = GetCurrentAuthType();
  const dispatch = useDispatch();
  const calendarAccess = useSelector(selectCalendarAccess);
  const lifecycle = useSelector(selectLifecycle);
  const currentUser = useSelector(selectCurrentUser);

  const manuallyCheckStatus = () => {
    if (machineStatus === SyncStatus.Active || machineStatus === SyncStatus.NA) {
      return;
    }
    if (machineStatus === SyncStatus.Disabled) {
      dispatch(openDialog(DialogIdentifiers.AzureCalendarSyncDisabledDialog));
      return;
    }
    dispatch(requestCalendarAccess({ source: 'manual' }));
  };

  // Background check must stay silent-only ('auto'): dispatching 'manual' here
  // counts as a user gesture, so the gateway may escalate to an
  // authentication.authenticate popup (Teams) or acquireTokenRedirect (web)
  // with no click behind it — in Teams that dialog can never settle, leaving
  // calendar='pending' (spinner) forever. Keyed on the flag + lifecycle rather
  // than the recreated callback so it doesn't re-fire on every render.
  useEffect(() => {
    if (currentUser?.enableOutlookSync && lifecycle === 'sessionReady') {
      dispatch(requestCalendarAccess({ source: 'auto' }));
    }
  }, [currentUser?.enableOutlookSync, lifecycle, dispatch]);

  const machineStatus = calendarAccessToSyncStatus(calendarAccess, lifecycle, authType);

  return {
    calendarSyncStatus: machineStatus,
    manuallyCheckStatus,
  };
}
